import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Building2, Calendar, Euro, ExternalLink, User, History, TrendingUp } from 'lucide-react';

const stageColors = {
  'Detectada': 'bg-blue-100 text-blue-800',
  'Propuesta': 'bg-yellow-100 text-yellow-800',
  'Presentada': 'bg-purple-100 text-purple-800',
  'Ganada': 'bg-green-100 text-green-800',
  'Perdida': 'bg-red-100 text-red-800',
  'Descartada': 'bg-gray-100 text-gray-800',
  'Lead': 'bg-blue-100 text-blue-800',
  'Qualified': 'bg-yellow-100 text-yellow-800',
  'Proposal': 'bg-purple-100 text-purple-800',
  'Negotiation': 'bg-orange-100 text-orange-800',
  'Won': 'bg-green-100 text-green-800',
  'Lost': 'bg-red-100 text-red-800',
};

const relevanceColors = {
  'Alta': 'bg-red-100 text-red-800',
  'Media': 'bg-yellow-100 text-yellow-800',
  'Baja': 'bg-green-100 text-green-800',
};

const OpportunityViewDialog = ({ open, onOpenChange, opportunity, companyName }) => {
  if (!opportunity) return null;

  const title = opportunity.title || opportunity.name || 'Sin título';
  const stage = opportunity.stage || 'Detectada';
  const isResultStage = stage === 'Ganada' || stage === 'Perdida';
  const isDiscarded = stage === 'Descartada';

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `€${value.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-ES');
  };

  const renderLink = (url, label) => {
    if (!url) return null;
    return (
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="w-4 h-4" />
        {label}
      </a>
    );
  };

  const InfoRow = ({ label, value, isLink = false, linkLabel = '' }) => {
    if (!value && value !== 0) return null;
    return (
      <div className="py-2">
        <span className="text-sm text-muted-foreground">{label}:</span>
        {isLink ? (
          <span className="ml-2">{renderLink(value, linkLabel || 'Ver enlace')}</span>
        ) : (
          <span className="ml-2 font-medium">{value}</span>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="opportunity-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3">
            <div className="flex-1">
              <h2 className="text-xl font-semibold">{title}</h2>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={stageColors[stage] || 'bg-gray-100'}>{stage}</Badge>
                {opportunity.relevance && (
                  <Badge className={relevanceColors[opportunity.relevance]}>{opportunity.relevance}</Badge>
                )}
                {opportunity.probability && (
                  <Badge variant="outline">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    {opportunity.probability}
                  </Badge>
                )}
              </div>
              {companyName && (
                <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                  <Building2 className="w-4 h-4" />
                  {companyName}
                </p>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-4">
            {/* Información General */}
            <div>
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2">
                Información General
              </h3>
              <div className="bg-muted/30 rounded-lg p-4 space-y-1">
                {opportunity.description && (
                  <div className="py-2">
                    <span className="text-sm text-muted-foreground">Descripción:</span>
                    <p className="mt-1">{opportunity.description}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Valor Estimado:</span>
                    <p className="text-lg font-semibold text-green-600">{formatCurrency(opportunity.estimated_value)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Fecha Límite de Entrega:</span>
                    <p>
                      {formatDate(opportunity.expected_delivery_date || opportunity.expected_close_date)}
                      {opportunity.deadline_time && <span className="ml-2 text-muted-foreground">a las {opportunity.deadline_time}</span>}
                    </p>
                  </div>
                </div>
                {opportunity.owner && (
                  <InfoRow label="Responsable" value={opportunity.owner} />
                )}
                
                {/* Links */}
                <div className="pt-2 space-y-1">
                  {renderLink(opportunity.link_pliego, 'Ver Pliego')}
                  {renderLink(opportunity.link_infografia, 'Ver Infografía')}
                  {renderLink(opportunity.link_pdf_resumen, 'Ver PDF Resumen')}
                  {renderLink(opportunity.link_notebooklm, 'Ver NotebookLM')}
                </div>
              </div>
            </div>

            {/* Preparación de Propuesta */}
            {['Propuesta', 'Presentada', 'Ganada', 'Perdida', 'Descartada'].includes(stage) && (
              <>
                <Separator />
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2">
                    Preparación de Propuesta
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-muted-foreground">Relevancia:</span>
                        <p className="font-semibold">{opportunity.relevance || '-'}</p>
                      </div>
                      {opportunity.relevance_comment && (
                        <div>
                          <span className="text-sm text-muted-foreground">Comentario:</span>
                          <p>{opportunity.relevance_comment}</p>
                        </div>
                      )}
                    </div>
                    {renderLink(opportunity.link_sharepoint, 'Ver SharePoint de la Oferta')}
                    {opportunity.comments && (
                      <div className="pt-2">
                        <span className="text-sm text-muted-foreground">Comentarios:</span>
                        <p className="mt-1 whitespace-pre-wrap">{opportunity.comments}</p>
                      </div>
                    )}
                    {!opportunity.link_sharepoint && !opportunity.comments && !opportunity.relevance && (
                      <p className="text-sm text-muted-foreground">Sin información adicional</p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Presentación de Oferta */}
            {['Presentada', 'Ganada', 'Perdida'].includes(stage) && (
              <>
                <Separator />
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2">
                    Presentación de Oferta
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-muted-foreground">Probabilidades:</span>
                        <p className="font-semibold">{opportunity.probability || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Importe Ofertado:</span>
                        <p className="font-semibold">{formatCurrency(opportunity.offered_amount)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Importe TDS:</span>
                        <p>{formatCurrency(opportunity.tds_amount)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Fecha de Entrega Real:</span>
                        <p>{formatDate(opportunity.delivery_date)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Licitador:</span>
                        <p>{opportunity.bidder || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Porcentaje de Bajada:</span>
                        <p className="font-semibold">
                          {opportunity.discount_percentage !== null && opportunity.discount_percentage !== undefined 
                            ? `${opportunity.discount_percentage}%` 
                            : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Resultado Final */}
            {isResultStage && (
              <>
                <Separator />
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Resultado Final
                  </h3>
                  <div className={`rounded-lg p-4 ${stage === 'Ganada' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-muted-foreground">Adjudicatario Final:</span>
                        <p className="font-semibold">{opportunity.final_awardee || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Importe Adjudicación:</span>
                        <p className="font-semibold text-green-600">{formatCurrency(opportunity.award_amount)}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Baja Adjudicatario:</span>
                        <p>{opportunity.awardee_discount !== null ? `${opportunity.awardee_discount}%` : '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Nº Licitadores:</span>
                        <p>{opportunity.number_of_bidders || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Posición Global:</span>
                        <p>{opportunity.global_position || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Posición Técnica:</span>
                        <p>{opportunity.technical_position || '-'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-muted-foreground">Posición Económica:</span>
                        <p>{opportunity.economic_position || '-'}</p>
                      </div>
                    </div>
                    {opportunity.result_comments && (
                      <div className="mt-3 pt-3 border-t border-current/10">
                        <span className="text-sm text-muted-foreground">Comentarios del Resultado:</span>
                        <p className="mt-1 whitespace-pre-wrap">{opportunity.result_comments}</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Motivo de Descarte */}
            {isDiscarded && opportunity.discard_reason && (
              <>
                <Separator />
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2">
                    Motivo del Descarte
                  </h3>
                  <div className="bg-gray-100 rounded-lg p-4">
                    <p className="whitespace-pre-wrap">{opportunity.discard_reason}</p>
                  </div>
                </div>
              </>
            )}

            {/* Historial de Estados */}
            {opportunity.state_history && opportunity.state_history.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Historial de Estados
                  </h3>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="space-y-3">
                      {[...opportunity.state_history].reverse().map((change, index) => (
                        <div 
                          key={index}
                          className="relative pl-4 border-l-2 border-muted pb-3 last:pb-0"
                        >
                          <div className="absolute -left-[5px] top-0 w-2 h-2 rounded-full bg-primary" />
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {change.previous_state && (
                                <>
                                  <Badge variant="outline" className="text-xs">{change.previous_state}</Badge>
                                  <span className="text-xs text-muted-foreground">→</span>
                                </>
                              )}
                              <Badge className={stageColors[change.new_state] || 'bg-gray-100'}>
                                {change.new_state}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {formatDateTime(change.timestamp)}
                            </p>
                            <p className="text-sm">Por: {change.changed_by_name}</p>
                            {change.owner && (
                              <p className="text-xs text-muted-foreground">Responsable: {change.owner}</p>
                            )}
                            {change.reason && (
                              <p className="text-sm mt-1 p-2 bg-muted rounded text-muted-foreground">
                                {change.reason}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Metadata */}
            <Separator />
            <div className="text-sm text-muted-foreground space-y-1">
              {opportunity.created_by_name && (
                <p>Creado por: {opportunity.created_by_name}</p>
              )}
              {opportunity.created_at && (
                <p>Fecha creación: {formatDateTime(opportunity.created_at)}</p>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default OpportunityViewDialog;
